import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navigation.css';
import NavLogoMark from './NavLogoMark';
import { getRouteAccent } from '../../transition';
import {
  HOME_SECTIONS,
  NAV_PROBE_OFFSET,
  readPendingHomeSection,
  scrollToHomeSection,
  sectionDocumentTop,
} from '../../navigation/homeSections';

const SECTION_ORDER = ['about', 'projects', 'services', 'resume-nav', 'contact'];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeNav, setActiveNav] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [showUnderline, setShowUnderline] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navListRef = useRef(null);
  const navLinkRailRef = useRef(null);
  const navItemRefs = useRef([]);
  const scrollRafRef = useRef(0);

  const updateUnderlinePosition = useCallback(() => {
    const rail = navLinkRailRef.current;
    if (!rail || !showUnderline) return;
    const idx = SECTION_ORDER.indexOf(activeSection);
    // Contact uses the CTA chip — no hairline under it
    if (idx < 0 || activeSection === 'contact') {
      rail.style.setProperty('--nav-underline-opacity', '0');
      return;
    }
    const li = navItemRefs.current[idx];
    if (!li) return;
    const link = li.querySelector('a');
    const target = link || li;
    const railRect = rail.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const styles = window.getComputedStyle(target);
    const padL = parseFloat(styles.paddingLeft) || 0;
    const padR = parseFloat(styles.paddingRight) || 0;
    const left = targetRect.left - railRect.left + padL;
    const width = Math.max(targetRect.width - padL - padR, 18);
    rail.style.setProperty('--nav-underline-x', `${left}px`);
    rail.style.setProperty('--nav-underline-w', `${width}px`);
    rail.style.setProperty('--nav-underline-opacity', '1');
  }, [activeSection, showUnderline]);

  useLayoutEffect(() => {
    updateUnderlinePosition();
  }, [updateUnderlinePosition, location.pathname]);

  useEffect(() => {
    const onResize = () => updateUnderlinePosition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updateUnderlinePosition]);

  useEffect(() => {
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => updateUnderlinePosition());
    }
  }, [updateUnderlinePosition]);

  const resolveActiveSection = useCallback(() => {
    if (location.pathname === '/resume') {
      setActiveSection('resume-nav');
      return;
    }
    if (location.pathname !== '/') {
      return;
    }

    const scrollY = window.scrollY;
    const docHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    );
    const atBottom = window.innerHeight + scrollY >= docHeight - 48;
    if (atBottom) {
      setActiveSection('contact');
      return;
    }

    const probe = scrollY + NAV_PROBE_OFFSET;
    let next = '';
    for (const id of HOME_SECTIONS) {
      const el = document.getElementById(id);
      if (!el) continue;
      if (probe >= sectionDocumentTop(el) - 8) {
        next = id;
      }
    }
    setActiveSection((prev) => (prev === next ? prev : next));
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      if (scrollTop > 24) {
        setScrolled(true);
      } else if (scrollTop < 8) {
        setScrolled(false);
      }

      if (scrollRafRef.current) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = 0;
        resolveActiveSection();
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resolveActiveSection);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resolveActiveSection);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, [resolveActiveSection]);

  useEffect(() => {
    if (location.pathname === '/') {
      const pending = readPendingHomeSection(location);
      if (pending) {
        setActiveSection(pending);
        setShowUnderline(true);
        // Cross-route scrolls are handled in App under the wipe seal.
        // Same-route / cold hash loads still scroll here.
        if (!location.state?.scrollTo) {
          const id = window.setTimeout(() => {
            scrollToHomeSection(pending, { behavior: 'smooth' });
          }, 300);
          return () => window.clearTimeout(id);
        }
        return undefined;
      }

      setShowUnderline(false);
      const showId = window.setTimeout(() => {
        setShowUnderline(true);
        resolveActiveSection();
      }, 1200);
      return () => window.clearTimeout(showId);
    }

    if (location.pathname === '/resume') {
      setActiveSection('resume-nav');
      setShowUnderline(true);
      return undefined;
    }

    setShowUnderline(false);
    return undefined;
  }, [location, resolveActiveSection]);

  useEffect(() => {
    if (activeNav) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [activeNav]);

  /**
   * Same page → smooth scroll.
   * Other page → navigate home with scrollTo state; App jumps under the wipe.
   */
  const handleSectionNav = (e, sectionId) => {
    e.preventDefault();
    setActiveNav(false);
    setActiveSection(sectionId);
    setShowUnderline(true);

    if (location.pathname === '/') {
      scrollToHomeSection(sectionId, { behavior: 'smooth' });
      return;
    }

    navigate('/', { state: { scrollTo: sectionId } });
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setActiveNav(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSection('');
    } else {
      navigate('/');
      setActiveSection('');
    }
  };

  const projectAccent = getRouteAccent(location.pathname);

  return (
    <>
      {activeNav && (
        <button
          type="button"
          className="navMobileBackdrop"
          aria-label="Close menu"
          onClick={() => setActiveNav(false)}
        />
      )}
      <nav
        className={`siteNav ${scrolled ? 'siteNav--scrolled' : ''} ${activeNav ? 'siteNav--menuOpen' : ''}`}
        aria-label="Primary"
      >
        <div className="navContainer">
          <Link
            to="/"
            className="navLogo"
            onClick={handleLogoClick}
            aria-label="Kevin Fuller, home"
          >
            <span className="navLogoMark" aria-hidden="true">
              <NavLogoMark className="navLogoSvg" accent={projectAccent} />
            </span>
          </Link>

          <div className="navLinkRail" ref={navLinkRailRef}>
            <ul
              ref={navListRef}
              id="primary-navigation"
              className={`navLinks ${activeNav ? 'navLinks--open' : ''}`}
            >
              <li
                className="navItem spot1"
                ref={(el) => {
                  navItemRefs.current[0] = el;
                }}
              >
                <span className="navItemIndex" aria-hidden="true">01</span>
                <Link
                  to="/"
                  className={activeSection === 'about' ? 'active-nav' : ''}
                  onClick={(e) => handleSectionNav(e, 'about')}
                >
                  About
                </Link>
              </li>
              <li
                className="navItem spot2"
                ref={(el) => {
                  navItemRefs.current[1] = el;
                }}
              >
                <span className="navItemIndex" aria-hidden="true">02</span>
                <Link
                  to="/"
                  className={activeSection === 'projects' ? 'active-nav' : ''}
                  onClick={(e) => handleSectionNav(e, 'projects')}
                >
                  Projects
                </Link>
              </li>
              <li
                className="navItem spot3"
                ref={(el) => {
                  navItemRefs.current[2] = el;
                }}
              >
                <span className="navItemIndex" aria-hidden="true">03</span>
                <Link
                  to="/"
                  className={activeSection === 'services' ? 'active-nav' : ''}
                  onClick={(e) => handleSectionNav(e, 'services')}
                >
                  Services
                </Link>
              </li>
              <li
                className="navItem spot4"
                ref={(el) => {
                  navItemRefs.current[3] = el;
                }}
              >
                <span className="navItemIndex" aria-hidden="true">04</span>
                <Link
                  to="/resume"
                  className={activeSection === 'resume-nav' ? 'active-nav' : ''}
                  onClick={() => setActiveNav(false)}
                >
                  Résumé
                </Link>
              </li>
              <li
                className="navItem navItem--cta spot5"
                ref={(el) => {
                  navItemRefs.current[4] = el;
                }}
              >
                <span className="navItemIndex" aria-hidden="true">05</span>
                <Link
                  to="/"
                  className={`navCta ${activeSection === 'contact' ? 'active-nav' : ''}`}
                  onClick={(e) => handleSectionNav(e, 'contact')}
                >
                  Contact
                </Link>
              </li>
            </ul>
            {showUnderline && (
              <div
                className={`animatedUnderline ${activeSection}`}
                aria-hidden="true"
              />
            )}
          </div>

          <button
            type="button"
            onClick={() => setActiveNav(!activeNav)}
            className={activeNav ? 'navMobileToggleOff' : 'navMobileToggleOn'}
            aria-expanded={activeNav}
            aria-controls="primary-navigation"
            aria-label={activeNav ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <span className="navCircle topLeft" />
            <span className="navCircle topMid" />
            <span className="navCircle topRight" />
            <span className="navCircle midLeft" />
            <span className="navCircle midMid" />
            <span className="navCircle midMid2" />
            <span className="navCircle midRight" />
            <span className="navCircle botLeft" />
            <span className="navCircle botMid" />
            <span className="navCircle botRight" />
          </button>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
